/**
 * Refund tab grid helpers for Case 59 (`tabOrderSlices.Refunded.RefundOrders`).
 * Standalone module — do not import from `STOrderDetailsModel` (avoids circular exports).
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
function pickString(raw, pascal, camel) {
    var _a;
    var value = (_a = raw[pascal]) !== null && _a !== void 0 ? _a : raw[camel];
    return value == null ? "" : String(value).trim();
}
/** Reads a positive numeric id from JSON (number or string); preserves full digits for URL `data=35`. */
function pickIdField(raw) {
    var keys = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        keys[_i - 1] = arguments[_i];
    }
    for (var _a = 0, keys_1 = keys; _a < keys_1.length; _a++) {
        var key = keys_1[_a];
        var value = raw[key];
        if (value == null) {
            continue;
        }
        var trimmed = String(value).trim();
        if (trimmed && trimmed !== "0") {
            return trimmed;
        }
    }
    return "";
}
function toPositiveIdNumber(id) {
    var trimmed = String(id !== null && id !== void 0 ? id : "").trim();
    if (!trimmed || trimmed === "0") {
        return 0;
    }
    var n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? n : 0;
}
function pickRefundId(raw) {
    return pickIdField(raw, "RefundId", "refundId", "refund_id");
}
function pickCreatedDate(raw) {
    var trimmed = pickString(raw, "CreatedDate", "createdDate");
    if (!trimmed || trimmed.toLowerCase() === "null") {
        return "";
    }
    return trimmed;
}
function pickOrderId(raw) {
    return pickIdField(raw, "OrderId", "orderId", "order_id", "RefundApiOrderId", "refundApiOrderId", "ShopifyOrderId", "shopifyOrderId");
}
function pickOrderNo(raw) {
    var direct = pickString(raw, "OrderNo", "orderNo");
    if (direct) {
        return direct;
    }
    return pickString(raw, "OrderNumber", "orderNumber");
}
function normalizeOrderNoKey(orderNo) {
    return orderNo.replace(/^#/, "").trim();
}
function collectOrderIdByOrderNoFromRows(rows, lookup) {
    if (!Array.isArray(rows)) {
        return;
    }
    for (var _i = 0, rows_1 = rows; _i < rows_1.length; _i++) {
        var raw = rows_1[_i];
        if (!raw || typeof raw !== "object") {
            continue;
        }
        var row = raw;
        var orderNo = normalizeOrderNoKey(pickOrderNo(row));
        var orderId = pickOrderId(row);
        if (orderNo && orderId && !lookup.has(orderNo)) {
            lookup.set(orderNo, orderId);
        }
    }
}
/** Normalises loose API JSON into `StReportsRefundGridRow`. */
export function parseStReportsRefundGridRow(raw) {
    var _a, _b, _c, _d, _e, _f;
    return {
        RefundId: pickRefundId(raw),
        OrderId: pickOrderId(raw),
        CreatedDate: pickCreatedDate(raw),
        OrderNo: pickOrderNo(raw),
        Commission: Number((_b = (_a = raw.Commission) !== null && _a !== void 0 ? _a : raw.commission) !== null && _b !== void 0 ? _b : 0),
        TeamManagerCommission: Number((_d = (_c = raw.TeamManagerCommission) !== null && _c !== void 0 ? _c : raw.teamManagerCommission) !== null && _d !== void 0 ? _d : 0),
        RefundSubTotal: Number((_f = (_e = raw.RefundSubTotal) !== null && _e !== void 0 ? _e : raw.refundSubTotal) !== null && _f !== void 0 ? _f : 0),
        OrderStatus: pickString(raw, "OrderStatus", "orderStatus"),
        SalesMethod: pickString(raw, "SalesMethod", "salesMethod"),
    };
}
function collectOrderIdByRefundIdFromRows(rows, lookup) {
    if (!Array.isArray(rows)) {
        return;
    }
    for (var _i = 0, rows_2 = rows; _i < rows_2.length; _i++) {
        var raw = rows_2[_i];
        if (!raw || typeof raw !== "object") {
            continue;
        }
        var row = raw;
        var refundId = pickRefundId(row);
        var orderId = pickOrderId(row);
        if (refundId && orderId) {
            lookup.set(refundId, orderId);
        }
    }
}
/** `RefundId` → Shopify `OrderId` from every refund-shaped bucket in the `data=59` payload. */
function buildOrderIdByRefundIdLookup(reportsData) {
    var _a, _b, _c, _d, _e, _f, _g;
    var lookup = new Map();
    var slicesRaw = (_a = reportsData.tabOrderSlices) !== null && _a !== void 0 ? _a : reportsData.TabOrderSlices;
    if (slicesRaw && typeof slicesRaw === "object") {
        var refundedRaw = (_b = slicesRaw.refunded) !== null && _b !== void 0 ? _b : slicesRaw.Refunded;
        if (refundedRaw && typeof refundedRaw === "object") {
            var refunded = refundedRaw;
            collectOrderIdByRefundIdFromRows(((_c = refunded.refundOrders) !== null && _c !== void 0 ? _c : refunded.RefundOrders), lookup);
        }
    }
    var exportBuckets = (_d = reportsData.exportOrderBuckets) !== null && _d !== void 0 ? _d : reportsData.ExportOrderBuckets;
    if (exportBuckets && typeof exportBuckets === "object") {
        var buckets = exportBuckets;
        collectOrderIdByRefundIdFromRows(((_e = buckets.refundRows) !== null && _e !== void 0 ? _e : buckets.RefundRows), lookup);
        collectOrderIdByRefundIdFromRows(((_f = buckets.refunded) !== null && _f !== void 0 ? _f : buckets.Refunded), lookup);
    }
    collectOrderIdByRefundIdFromRows(((_g = reportsData.refundRows) !== null && _g !== void 0 ? _g : reportsData.RefundRows), lookup);
    return lookup;
}
function resolveOrderIdForRefundRow(item, refundIdStr, reportsData) {
    var _a, _b, _c;
    var orderIdRaw = (_c = (_b = (_a = item.RefundApiOrderId) !== null && _a !== void 0 ? _a : item.refundApiOrderId) !== null && _b !== void 0 ? _b : item.orderId) !== null && _c !== void 0 ? _c : item.OrderId;
    var orderIdStr = orderIdRaw != null ? String(orderIdRaw).trim() : "";
    if (orderIdStr && orderIdStr !== "0") {
        return orderIdStr;
    }
    if (reportsData && typeof reportsData === "object") {
        var fromRefundId = buildOrderIdByRefundIdLookup(reportsData).get(refundIdStr);
        if (fromRefundId) {
            return fromRefundId;
        }
        var orderNoKey = normalizeOrderNoKey(pickOrderNo(item));
        if (orderNoKey) {
            var fromOrderNo = buildRefundGridOrderIdByOrderNoLookup(reportsData).get(orderNoKey);
            if (fromOrderNo) {
                return fromOrderNo;
            }
        }
    }
    return "";
}
/** Resolves GET `data=35` params from a refund grid / legacy refund row. */
export function resolveRefundOrderDetailRequestParams(item, reportsData) {
    var _a;
    if (!item || typeof item !== "object") {
        return null;
    }
    var refundIdRaw = (_a = item.RefundId) !== null && _a !== void 0 ? _a : item.refundId;
    var refundIdStr = refundIdRaw != null ? String(refundIdRaw).trim() : "";
    if (!refundIdStr || refundIdStr === "0") {
        return null;
    }
    var orderIdStr = resolveOrderIdForRefundRow(item, refundIdStr, reportsData);
    if (!orderIdStr || orderIdStr === "0") {
        return null;
    }
    var refundId = Number(refundIdStr);
    var orderId = Number(orderIdStr);
    if (!Number.isFinite(refundId) || refundId <= 0) {
        return null;
    }
    if (!Number.isFinite(orderId) || orderId <= 0) {
        return null;
    }
    return { refundId: refundId, orderId: orderId };
}
/** When refund grid rows omit `OrderId`, resolve it from sibling All/Paid rows with the same order number. */
function buildRefundGridOrderIdByOrderNoLookup(reportsData) {
    var _a, _b, _c, _d;
    var lookup = new Map();
    var slicesRaw = (_a = reportsData.tabOrderSlices) !== null && _a !== void 0 ? _a : reportsData.TabOrderSlices;
    if (slicesRaw && typeof slicesRaw === "object") {
        var slices = slicesRaw;
        for (var _i = 0, _e = ["all", "paid", "unpaid", "draft", "All", "Paid", "Unpaid", "Draft"]; _i < _e.length; _i++) {
            var key = _e[_i];
            var slice = slices[key];
            if (!slice || typeof slice !== "object") {
                continue;
            }
            var orders = (_b = slice.orders) !== null && _b !== void 0 ? _b : slice.Orders;
            collectOrderIdByOrderNoFromRows(orders, lookup);
        }
    }
    var top = (_c = reportsData.Orders) !== null && _c !== void 0 ? _c : reportsData.orders;
    collectOrderIdByOrderNoFromRows(top, lookup);
    var exportBuckets = (_d = reportsData.exportOrderBuckets) !== null && _d !== void 0 ? _d : reportsData.ExportOrderBuckets;
    if (exportBuckets && typeof exportBuckets === "object") {
        var buckets = exportBuckets;
        for (var _f = 0, _g = ["all", "paid", "unpaid", "draft", "refunded", "All", "Paid", "Unpaid", "Draft", "Refunded"]; _f < _g.length; _f++) {
            var key = _g[_f];
            collectOrderIdByOrderNoFromRows(buckets[key], lookup);
        }
    }
    return lookup;
}
function enrichRefundGridRowOrderId(row, orderIdByRefundId, orderIdByOrderNo) {
    if (row.OrderId) {
        return row;
    }
    if (row.RefundId) {
        var fromRefundId = orderIdByRefundId.get(row.RefundId);
        if (fromRefundId) {
            return __assign(__assign({}, row), { OrderId: fromRefundId });
        }
    }
    var orderNoKey = normalizeOrderNoKey(row.OrderNo);
    if (orderNoKey) {
        var fromOrderNo = orderIdByOrderNo.get(orderNoKey);
        if (fromOrderNo) {
            return __assign(__assign({}, row), { OrderId: fromOrderNo });
        }
    }
    return row;
}
/** Reads refund rows from `tabOrderSlices.Refunded`, export buckets, or root `RefundRows`. */
export function getSTOrderDetailsRefundRowsFromPayload(reportsData) {
    var _a, _b, _c, _d, _e, _f;
    if (!reportsData || typeof reportsData !== "object") {
        return [];
    }
    var orderIdByRefundId = buildOrderIdByRefundIdLookup(reportsData);
    var orderIdByOrderNo = buildRefundGridOrderIdByOrderNoLookup(reportsData);
    var finalize = function (rows) { return rows.map(function (row) { return enrichRefundGridRowOrderId(row, orderIdByRefundId, orderIdByOrderNo); }); };
    var slicesRaw = (_a = reportsData.tabOrderSlices) !== null && _a !== void 0 ? _a : reportsData.TabOrderSlices;
    if (slicesRaw && typeof slicesRaw === "object") {
        var slices = slicesRaw;
        var refundedRaw = (_b = slices.refunded) !== null && _b !== void 0 ? _b : slices.Refunded;
        if (refundedRaw && typeof refundedRaw === "object") {
            var refunded = refundedRaw;
            var fromSlice = (_c = refunded.refundOrders) !== null && _c !== void 0 ? _c : refunded.RefundOrders;
            if (Array.isArray(fromSlice) && fromSlice.length > 0) {
                return finalize(fromSlice.map(function (row) { return parseStReportsRefundGridRow(row); }));
            }
        }
    }
    var exportBuckets = (_d = reportsData.exportOrderBuckets) !== null && _d !== void 0 ? _d : reportsData.ExportOrderBuckets;
    if (exportBuckets && typeof exportBuckets === "object") {
        var buckets = exportBuckets;
        var fromExport = (_e = buckets.refundRows) !== null && _e !== void 0 ? _e : buckets.RefundRows;
        if (Array.isArray(fromExport) && fromExport.length > 0) {
            return finalize(fromExport.map(function (row) { return parseStReportsRefundGridRow(row); }));
        }
    }
    var fromRoot = (_f = reportsData.refundRows) !== null && _f !== void 0 ? _f : reportsData.RefundRows;
    if (Array.isArray(fromRoot) && fromRoot.length > 0) {
        return finalize(fromRoot.map(function (row) { return parseStReportsRefundGridRow(row); }));
    }
    return [];
}
/** Maps `StReportsRefundGridRow` into unified `All` tab order row shape. */
export function mapRefundGridRowToStReportsOrderRow(row) {
    var refundId = row.RefundId;
    var parsedOrderId = toPositiveIdNumber(row.OrderId);
    return {
        orderNumber: row.OrderNo,
        OrderNumber: row.OrderNo,
        orderId: parsedOrderId,
        OrderId: parsedOrderId,
        customerName: null,
        CustomerName: null,
        orderDate: row.CreatedDate || null,
        OrderDate: row.CreatedDate || null,
        orderPaidDate: null,
        OrderPaidDate: null,
        subtotal: row.RefundSubTotal,
        Subtotal: row.RefundSubTotal,
        commission: row.Commission,
        Commission: row.Commission,
        companyName: null,
        CompanyName: null,
        companyLocation: null,
        CompanyLocation: null,
        salesMethod: row.SalesMethod || null,
        SalesMethod: row.SalesMethod || null,
        fulfilmentStatus: null,
        FulfilmentStatus: null,
        status: row.OrderStatus || "refunded",
        Status: row.OrderStatus || "refunded",
        refundId: refundId || null,
        RefundId: refundId || null,
        refundApiOrderId: row.OrderId || null,
        RefundApiOrderId: row.OrderId || null,
    };
}
function dedupeAllTabOrderRowKey(row) {
    var _a, _b, _c, _d, _e, _f;
    var refundId = String((_b = (_a = row.refundId) !== null && _a !== void 0 ? _a : row.RefundId) !== null && _b !== void 0 ? _b : "").trim();
    if (refundId) {
        return "refund:".concat(refundId);
    }
    var orderNum = String((_d = (_c = row.orderNumber) !== null && _c !== void 0 ? _c : row.OrderNumber) !== null && _d !== void 0 ? _d : "").trim();
    var orderId = String((_f = (_e = row.orderId) !== null && _e !== void 0 ? _e : row.OrderId) !== null && _f !== void 0 ? _f : "").trim();
    return "order:".concat(orderNum, "|").concat(orderId);
}
/** All tab must include refunds from the Refunded slice (API keeps them separate). */
export function mergeRefundOrdersIntoAllTabOrders(baseOrders, refundRows) {
    if (!Array.isArray(baseOrders) || baseOrders.length === 0) {
        if (!refundRows.length) {
            return [];
        }
        return refundRows.map(function (row) { return mapRefundGridRowToStReportsOrderRow(row); });
    }
    if (!refundRows.length) {
        return baseOrders;
    }
    var seen = new Set(baseOrders.map(function (row) { return dedupeAllTabOrderRowKey(row); }));
    var merged = __spreadArray([], baseOrders, true);
    for (var _i = 0, refundRows_1 = refundRows; _i < refundRows_1.length; _i++) {
        var refund = refundRows_1[_i];
        var orderRow = mapRefundGridRowToStReportsOrderRow(refund);
        var key = dedupeAllTabOrderRowKey(orderRow);
        if (!key || seen.has(key)) {
            continue;
        }
        seen.add(key);
        merged.push(orderRow);
    }
    return merged;
}
/** Maps `StReportsRefundGridRow` to the shape used by Refund tab UI / detail dialogs. */
export function mapStReportsRefundGridRowForUi(row) {
    var shopifyOrderId = row.OrderId;
    var parsedOrderId = toPositiveIdNumber(shopifyOrderId);
    return {
        RefundId: row.RefundId,
        CreatedDate: row.CreatedDate || "-",
        ShopifyOrderNumber: row.OrderNo,
        OrderNo: row.OrderNo,
        Commission: row.Commission,
        TeamManagerCommission: row.TeamManagerCommission,
        RefundSubTotal: row.RefundSubTotal,
        OrderStatus: row.OrderStatus,
        OrderType: row.SalesMethod,
        orderId: shopifyOrderId,
        OrderId: parsedOrderId,
        RefundApiOrderId: shopifyOrderId,
    };
}
export function getSTOrderDetailsRefundRowsForUiTab(reportsData, uiTab) {
    if (String(uiTab || "").trim().toLowerCase() !== "refunded") {
        return [];
    }
    return getSTOrderDetailsRefundRowsFromPayload(reportsData).map(mapStReportsRefundGridRowForUi);
}
